<template>
  <div>
    <div class="pt-6 visible">
      <h1 v-if="!loading" class="text-lg leading-tight pb-2 xl:text-2.5xl xl:pb-4">
        {{ $t('components.payment.title') }}
      </h1>

      <Loading v-if="loading && !errors.length">
        <p v-if="paymentProcessing" class="max-w-sm mx-auto text-center pt-6 xl:pt-12">
          {{ $t('components.payment.loading', brandInfo) }}
        </p>
      </Loading>

      <div v-if="errors.length" ref="error" class="w-full flex border border-gray-300 bg-white p-4 mt-2 mb-4">
        <div class="w-8 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" v-bind:svg-inline="''" v-bind:class="'w-4.5 h-4.5'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.6 0H5.269L0 5.4v7.2L5.4 18h7.2l5.4-5.4V5.4zm1.665 12.189l-2.121 2.121-3.189-3.19-3.189 3.19-2.122-2.121L6.837 9l-3.19-3.189 2.122-2.122 3.189 3.19 3.189-3.19 2.121 2.122L11.08 9z" fill="#c00"/></svg>
        </div>
        <div class="pt-2px xl:pt-0">
          <p class="font-bold text-sm leading-none pb-2 xl:text-lg">
            {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
          </p>
          <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
        </div>
      </div>

      <div v-if="!loading">
        <div v-if="!innerUltimateFailure" class="w-full border border-gray-300 bg-white mb-10">
          <div class="leading-tight px-3 py-2 xl:text-lg">
            {{ $t('components.payment.description') }}
          </div>

          <div class="pb-2" :class="[formReady ? 'visible' : 'hidden']">
            <FormInput
              v-model.trim="fields.name.value"
              :placeholder="$t('components.payment.form.name.placeholder')"
              :label="$t('components.payment.form.name.label')"
              :valid="fields.name.valid"
              :error-string="$t('components.payment.error.name')"
              extra-class="mb-5 px-3"
              @focus="resetFieldValidity('name')"
            />

            <div class="w-full mb-5">
              <div
                class="input-component w-full"
                :class="{
                  'is-active': fields.cardNumber.focused || fields.cardNumber.filled,
                  'is-error': !fields.cardNumber.valid,
                }"
              >
                <label class="text-xs text-gray-700 cursor-text px-3">
                  {{ $t('components.payment.form.cardNumber.label') }}
                </label>
                <div class="input relative secure-field">
                  <div id="card-number" />
                </div>

                <p v-if="!fields.cardNumber.valid" class="mt-4 ml-3">
                  <span class="input-error bg-error text-white text-xs leading-none px-2 py-px">
                    {{ $t('components.payment.error.cardNumber') }}
                  </span>
                </p>
              </div>
            </div>
            <div class="flex w-full mb-5">
              <div class="w-48 mr-4">
                <FormInput
                  v-model.trim="fields.expiryDate.value"
                  v-mask="'##/##'"
                  :placeholder="$t('components.payment.form.expiryDate.placeholder')"
                  :label="$t('components.payment.form.expiryDate.label')"
                  :valid="fields.expiryDate.valid"
                  :error-string="$t('components.payment.error.expiryDate')"
                  extra-class="pl-3"
                  @focus="resetFieldValidity('expiryDate')"
                />
              </div>
              <div class="w-48">
                <div
                  class="input-component w-full"
                  :class="{ 'is-active': fields.cvv.focused || fields.cvv.filled, 'is-error': !fields.cvv.valid }"
                >
                  <label class="text-xs text-gray-700 cursor-text px-3">
                    {{ $t('components.payment.form.cvv.label') }}
                  </label>
                  <div class="input relative secure-field">
                    <div id="cvv-number" />
                  </div>

                  <p v-if="!fields.cvv.valid" class="mt-4 ml-3">
                    <span class="input-error bg-error text-white text-xs leading-none px-2 py-px">
                      {{ $t('components.payment.error.cvv') }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <h1 class="text-lg leading-tight pb-2 xl:text-2.5xl xl:pb-4">
          {{ $t('components.summary.finalSummary.title') }}
        </h1>

        <div class="w-full border border-gray-300 border-b-0 bg-white mb-4">
          <div class="text-sm border-b-2 border-secondary xl:text-lg xl:border-b-3">
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.totalDue') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
              </div>
            </div>
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3">
                {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
              </div>
              <div class="flex-shrink-0 font-numeric">
                {{ redemption.vouchersBalance | lhFormatCurrency(currency, market) }}
              </div>
            </div>
            <div class="flex justify-between px-3 py-2">
              <div class="mr-3 font-bold">
                {{ $t('components.summary.finalSummary.creditCardAmount') }}
              </div>
              <div class="flex-shrink-0 font-bold font-numeric">
                {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>
        </div>

        <div class="flex pt-6">
          <Button
            variant="secondary"
            :loading="loading || secureFieldsProcessing || innerUltimateFailure"
            full-width
            @click="payment"
          >
            {{ $t('components.summary.submitButton') }}
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import Payment from '~psp-pages/base/redemption/payment.ts'

import Button from '~brand-components/lufthansa/button/button.vue'
import FormInput from '~brand-components/lufthansa/form-input/form-input.vue'
import Loading from '~brand-components/lufthansa/loading/loading.vue'

export default Payment.extend({
  components: {
    Button,
    FormInput,
    Loading,
  },
  computed: {
    datatransBorder(): string {
      return '#666'
    },
    datatransBorderError(): string {
      return '#c00'
    },
    datatransStyles(): Record<string, string> {
      return {
        '*': `
          width: calc(100% - 1.2rem);
          font-size: 1.125rem;
          color: #666666;
          background-color: transparent;
          height: 2.5rem;
          border-bottom-width: 2px;
          border-color: #666666;
          border-radius: 0;
          border-top-width: 0;
          border-left-width: 0;
          border-right-width: 0;
          -webkit-appearance: none;
          -moz-appearance: none;
          appearance: none;
          border-style: solid;
          margin: 0.6rem;
          outline: 0;
        `,
        '*:focus': `
          box-shadow: 0 0 0.6rem 0.1rem #52627c;
          padding-left: 0.75rem;
          padding-right: 0.75rem;
        `,
        '*::placeholder': 'color: rgba(102, 102, 102, 1); opacity: 1',
        '*:-ms-input-placeholder': 'color: #666666',
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.input-component {
  label {
    @apply opacity-0;

    opacity: 0;
  }
  .input {
    height: 2.5rem;
    &::before {
      position: absolute;
      right: 0;
      bottom: 0;
      width: 2px;
      height: 50%;
      margin-right: 0.6rem;
      content: '';
      background: #666;
      @apply bg-gray-600;
    }
  }
  &.is-active {
    label {
      @apply opacity-100;

      opacity: 1;
    }
    .input {
      &::before {
        display: none;
      }
    }
  }
  &.is-error {
    label {
      @apply opacity-100;

      opacity: 1;
    }
    .input {
      input {
        @apply border-error;

        border-color: #c00;
      }
      &::before {
        display: none;
      }
    }
  }
}

.secure-field {
  margin-top: -0.6rem;

  &::before {
    margin-bottom: -0.6rem;
  }
}
</style>
